import type { ClientIdentity } from "@hakili/contracts";
import { formatDate, formatMoney, t } from "@hakili/i18n";
import { useDemandeFonctionnalite } from "@hakili/ui";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

import type { DeclaredLatest } from "./eligibilite.js";
import type { Handsets } from "../session/useSession.js";

/**
 * `round CW`, `CW.1`: **« Mon compte », as the drawing draws it.**
 *
 * `hakili-client.jsx`'s `Profil`: the identity card, five rows, « Demander une
 * fonctionnalité », and « Se déconnecter » in rose. It replaces an `EcranAVenir`
 * stub, so the copy comes from the drawing rather than from the stub's own
 * words , the mockups ARE the design.
 *
 * ## The four rows whose doors do not exist keep the drawing's look
 *
 * `R81` as the founder amended it: a control before its door **keeps the
 * drawing's look, greyed, its reason in its title**. So « Mes documents »,
 * « Notifications » and « Aide » are rendered exactly where the drawing puts
 * them, dimmed and disabled, each saying why in its own `title`. Deleting them
 * would redraw the screen; leaving them live would promise a page that does not
 * exist.
 *
 * « Mes favoris » is the same shape and lands in `CW.2` with `P1-CAT-009`'s
 * doors; it is dimmed here for exactly as long as that.
 *
 * ## What is LIVE
 *
 * The identity card, from `/me` and never from a literal. « Mon dossier »,
 * which navigates to a tab that exists. « Demander une fonctionnalité », on the
 * door `CW.1` opened to the buyer. And « Se déconnecter », which is `6.2`: the
 * way out belongs on the surface.
 *
 * ## « Mes appareils », round `PB-2`, `M4`
 *
 * `P0-IDN-057` binds the PIN to the handsets the server issued a secret to, and `P1-IDN-058` tells an
 * existing handset when another is added or the PIN is reset through one: **the previous channel is the
 * handset**, because after a SIM swap the number is the attacker's. So what this handset was told is
 * shown HERE, in the api's own sentences, above the list; and a handset the client does not recognise is
 * retired from here , refused, in the api's words, while an added handset is cooling. The drawing has no
 * such section; it is flagged in the round's report rather than drawn into a row it does not have.
 */
export interface ProfilProps {
  readonly identity: ClientIdentity;
  /** `null` until a dossier exists , the drawing says « aucun dossier en cours ». */
  readonly dossier: { readonly label: string; readonly intention: string | null } | null;
  readonly naviguer: (to: string) => void;
  readonly deconnecter: () => void;
  /** Round `PB-2`, `M4`: `null` while the devices door has not answered , nothing is drawn then. */
  readonly handsets?: Handsets | null;
  readonly retire?: (deviceId: string) => Promise<void>;
  readonly markRead?: () => Promise<void>;
  /**
   * `round N+3`, `S2-14`: the client's own latest declaration , `null` while
   * none exists yet, which the row reads as « Non renseigné » rather than as
   * an absence the screen has to explain.
   */
  readonly declaration?: DeclaredLatest | null;
  /** `S2-14`: the door into `WF-CLI-05`'s own form, with no vehicle in mind. */
  readonly onUpdateDeclaration?: () => void;
  /**
   * `CLIENT-PROFILE-RETURN` (`P0-DOS-075`): the sentences the last profile save served (`PUT /me/profile` → `notices`), drawn verbatim as info notices, **once**: the screen keeps what it was handed
   * for as long as it stays mounted and tells the app (`onNoticesShown`) so the app does not hand them over again. Nothing is composed here; `[]` or absent draws nothing.
   */
  readonly notices?: readonly string[];
  readonly onNoticesShown?: () => void;
}

/** The two letters the drawing puts in the round chip, from the name it was given. */
export function initialesDe(nom: string): string {
  const mots = nom.trim().split(/\s+/u).filter(Boolean);
  if (mots.length === 0) return "";
  const premier = mots[0] ?? "";
  const dernier = mots.length === 1 ? "" : (mots[mots.length - 1] ?? "");
  return `${premier.slice(0, 1)}${dernier.slice(0, 1)}`.toLocaleUpperCase("fr-FR");
}

/** The label the drawing gives the dossier row, which follows the buyer's own intent. */
export function dossierRowLabel(intention: string | null): string {
  if (intention === "comptant") return t("client.moncompte.dossierComptant");
  if (intention === "financement") return t("client.moncompte.dossierFinancement");
  return t("client.moncompte.dossier");
}

/**
 * `round N+3`, `S2-14`: the new row's second line , the salary, the
 * situation and, when there is one, the existing loan, with the date
 * declared. `Non renseigné` when no declaration exists yet, the same way
 * `dossierRowLabel` above answers for a dossier that does not exist either.
 *
 * `employment` is rendered as the door's own wire value, never re-worded ,
 * `EMPLOYMENT_SITUATIONS` is the one vocabulary for it, and a label built
 * here would be a second one agreeing with the first by coincidence.
 */
export function situationSousDe(declaration: DeclaredLatest | null): string {
  if (declaration === null) return t("client.moncompte.situationAucune");
  const segments = [
    t("client.moncompte.situationResumeSalaire").replace(
      "{salaire}",
      formatMoney(declaration.netSalaryAmount),
    ),
    declaration.employment,
  ];
  if (declaration.existingLoanInstalmentAmount > 0) {
    segments.push(
      t("client.moncompte.situationResumePret").replace(
        "{pret}",
        formatMoney(declaration.existingLoanInstalmentAmount),
      ),
    );
  }
  const declared = t("client.moncompte.situationResumeDeclaree").replace(
    "{date}",
    formatDate(new Date(declaration.declaredAt)),
  );
  return `${segments.join(" · ")} · ${declared}`;
}

function Rangee({
  titre,
  sous,
  onClick,
  raison,
  testid,
}: {
  readonly titre: string;
  readonly sous: string;
  readonly onClick?: () => void;
  readonly raison?: string;
  readonly testid: string;
}): React.JSX.Element {
  const inerte = onClick === undefined;
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={inerte}
      {...(inerte ? { title: raison } : {})}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-left ${
        inerte ? "opacity-50" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{titre}</p>
        <p className="mt-0.5 text-xs text-gray-500">{sous}</p>
      </div>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
    </button>
  );
}

/** The handsets and what this one was told, each refusal in the api's own words. */
function HandsetsSection({
  handsets,
  retire,
  markRead,
}: {
  readonly handsets: Handsets;
  readonly retire: (deviceId: string) => Promise<void>;
  readonly markRead: () => Promise<void>;
}): React.JSX.Element {
  const [refusal, setRefusal] = useState<string | null>(null);
  const report = (failure: unknown): void => {
    setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
  };
  return (
    <div className="flex flex-col gap-2" data-testid="profil-appareils">
      {handsets.notices.length > 0 ? (
        <div
          className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
          role="status"
        >
          {handsets.notices.map((notice) => (
            <p key={notice.id} className="text-sm text-amber-700" data-testid="profil-avis">
              {notice.message}
            </p>
          ))}
          <button
            type="button"
            data-testid="profil-avis-lu"
            className="self-start rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-50"
            onClick={() => {
              void markRead().catch(report);
            }}
          >
            {t("client.moncompte.avisLu")}
          </button>
        </div>
      ) : null}
      <h2 className="mt-1 text-sm font-semibold text-gray-900">{t("client.moncompte.appareils")}</h2>
      {handsets.handsets.map((handset) => (
        <div
          key={handset.id}
          data-testid="profil-appareil"
          className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {handset.current ? t("client.moncompte.appareilCourant") : t("client.moncompte.appareilAutre")}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("client.moncompte.appareilAjoute").replace("{date}", formatDate(new Date(handset.addedAt)))}
            </p>
          </div>
          {handset.current ? null : (
            <button
              type="button"
              data-testid="profil-appareil-retirer"
              className="text-sm font-medium text-rose-600"
              onClick={() => {
                setRefusal(null);
                void retire(handset.id).catch(report);
              }}
            >
              {t("client.moncompte.appareilRetirer")}
            </button>
          )}
        </div>
      ))}
      {refusal === null ? null : (
        <p role="alert" className="text-sm text-rose-700" data-testid="profil-appareils-refus">
          {refusal}
        </p>
      )}
    </div>
  );
}

export function Profil({
  identity,
  dossier,
  naviguer,
  deconnecter,
  handsets = null,
  retire = () => Promise.resolve(),
  markRead = () => Promise.resolve(),
  declaration = null,
  onUpdateDeclaration,
  notices = [],
  onNoticesShown,
}: ProfilProps): React.JSX.Element {
  const { ouvrir } = useDemandeFonctionnalite();
  const sansPorte = t("client.moncompte.sansPorte");
  // `CLIENT-PROFILE-RETURN`: what the last save served is kept for as long as this screen stays mounted (drawn once), and the app is told so it hands it over no more.
  const [served] = useState<readonly string[]>(notices);
  useEffect(() => {
    if (served.length > 0) onNoticesShown?.();
    // Once, on mount: the app's callback is not a dependency, or the notice would be re-announced on every render.
  }, []);
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.moncompte.titre")}</h1>

      {served.map((sentence, index) => (
        <p
          key={`${String(index)}-${sentence}`}
          role="status"
          data-testid="profil-avis-declaration"
          className="rounded-xl bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
        >
          {sentence}
        </p>
      ))}

      <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4">
        <div
          className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700"
          data-testid="profil-initiales"
        >
          {initialesDe(identity.fullName)}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900" data-testid="profil-nom">
            {identity.fullName}
          </p>
          <p className="text-xs text-gray-500 tabular-nums" data-testid="profil-numero">
            {identity.phone}
          </p>
        </div>
      </div>

      <Rangee
        testid="profil-situation"
        titre={t("client.moncompte.situationTitre")}
        sous={situationSousDe(declaration)}
        {...(onUpdateDeclaration === undefined ? { raison: sansPorte } : { onClick: onUpdateDeclaration })}
      />

      {handsets === null ? null : <HandsetsSection handsets={handsets} retire={retire} markRead={markRead} />}

      {/* `P1-CAT-009`'s doors land in `CW.2`; until then the count would be a
          number nobody computed, which is exactly what `R81` forbids. */}
      <Rangee testid="profil-favoris" titre={t("client.moncompte.favoris")} sous="-" raison={sansPorte} />

      <Rangee
        testid="profil-dossier"
        titre={dossierRowLabel(dossier?.intention ?? null)}
        sous={dossier?.label ?? t("client.moncompte.dossierAucun")}
        onClick={() => {
          naviguer("/dossier");
        }}
      />

      <Rangee testid="profil-documents" titre={t("client.moncompte.documents")} sous="-" raison={sansPorte} />
      <Rangee
        testid="profil-notifications"
        titre={t("client.moncompte.notifications")}
        sous={t("client.moncompte.notificationsSous")}
        raison={sansPorte}
      />
      <Rangee
        testid="profil-aide"
        titre={t("client.moncompte.aide")}
        sous={t("client.moncompte.aideSous")}
        raison={sansPorte}
      />

      {/* LIVE, on the door `CW.1` opened to the buyer. `P1-UIK-025` as amended:
          a control is live, or it says why it is not , and this one is. */}
      <Rangee
        testid="profil-idee"
        titre={t("client.moncompte.idee")}
        sous={t("client.moncompte.ideeSous")}
        onClick={() => {
          ouvrir(`${identity.fullName} · ${t("client.moncompte.ideeContexte")}`);
        }}
      />

      {/* `6.2`: the way out lives on the surface. */}
      <button
        type="button"
        data-testid="profil-deconnexion"
        onClick={deconnecter}
        className="w-full rounded-2xl border border-rose-100 bg-white py-3.5 text-sm font-medium text-rose-600"
      >
        {t("client.moncompte.deconnexion")}
      </button>
    </div>
  );
}
